/**
 * 23 Oct 2026 (Dori): Spanish toggle for the whole app.
 *
 * - `t('English text', {vars})` returns the Spanish text when the user picked
 *   Spanish, else the English. The English text IS the key, so a missing
 *   translation simply shows English.
 * - The choice lives in localStorage ('zonoo.lang'), so it is remembered on
 *   this device. Switching reloads the page, so every screen and every
 *   cached answer comes back in the new language.
 * - Every request to /api/ carries `X-Zonoo-Lang`, so the server can give the
 *   AI texts (Today, This week, Zoe...) in Spanish (server/i18n.py).
 */
import { es as dfEs } from 'date-fns/locale'
import type { Locale } from 'date-fns'
import { ES } from './es'

export type Lang = 'en' | 'es'
const STORAGE_KEY = 'zonoo.lang'

let current: Lang = readStored()

function readStored(): Lang {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY) === 'es' ? 'es' : 'en'
  } catch {
    return 'en'
  }
}

export function getLang(): Lang {
  return current
}

/** Switch language and reload, so the whole app and its data follow. */
export function setLang(lang: Lang, reload = true): void {
  current = lang
  try {
    localStorage.setItem(STORAGE_KEY, lang)
  } catch {
    /* private mode: the choice lasts for this page only */
  }
  if (reload && typeof window !== 'undefined') window.location.reload()
}

/** Test helper: change the language without touching storage or reloading. */
export function __setLangForTests(lang: Lang): void {
  current = lang
}

function fill(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text
  return text.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m))
}

export function t(en: string, vars?: Record<string, string | number>): string {
  if (current === 'es') {
    const es = ES[en]
    if (es) return fill(es, vars)
  }
  return fill(en, vars)
}

/** Locale for dates and numbers shown to the user. */
export function dateLocale(): string {
  return current === 'es' ? 'es-ES' : 'en-GB'
}

let installed = false
/** Add `X-Zonoo-Lang` to every same-site /api/ request (fetch is patched once). */
export function installLangHeader(): void {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return
  installed = true
  if (typeof document !== 'undefined') document.documentElement.lang = current
  const original = window.fetch.bind(window)
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    const sameSiteApi = url.startsWith('/api/') || url.startsWith(`${window.location.origin}/api/`)
    if (!sameSiteApi || current === 'en') return original(input, init)
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
    headers.set('X-Zonoo-Lang', current)
    return original(input, { ...init, headers })
  }
}

/** date-fns locale for month/day names: pass as `format(d, 'd MMM', { locale: dfLocale() })`. */
export function dfLocale(): Locale | undefined {
  return current === 'es' ? dfEs : undefined
}
